import { types, flow } from 'mobx-state-tree'
import StripeThreeDSecureStore from "./BraintreeThreeDSecureStore"

import theme from '~/style/theme'

export const DEFAULT_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontFamily: theme.fonts.avenirNextDemi,
      fontSize: '16px',
      color: theme.colors.text.input,
      '::placeholder': {
        color: theme.colors.text.inputPlaceholder
      }
    },
    invalid: {
      color: theme.colors.text.input,
    }
  }
}

export const PLACEHOLDERS = {
  cardNumber: '•••• •••• •••• ••••',
  cardExpiry: 'MM/YY',
  cardCvc: '•••'
}

const loadScript = () => {
  if (!process.browser) {
    return Promise.reject(new Error('Load outside of browser'))
  }

  const getStripe = () => {
    const result = document.getElementById('stripe-js')
    return result
  }

  return new Promise((resolve, reject) => {
    const TIMEOUT = 30
    let count = 0
    let interval = null

    const refresh = () => {
      const foundStripe = getStripe()
      if (foundStripe && typeof Stripe !== 'undefined') {
        clearInterval(interval)
        return resolve()
      }
      if (count >= TIMEOUT) {
        clearInterval(interval)
        return reject(new Error('Timed out while loading stripe.'))
      }
      count += 1
    }

    interval = setInterval(refresh, 1000)
  })
}

const StripeStore = types
  .model('StripeStore', {
    isReady: types.optional(types.boolean, false),
    cardNumberIsValid: types.optional(types.boolean, false),
    cardExpiryIsValid: types.optional(types.boolean, false),
    cardCvcIsValid: types.optional(types.boolean, false),
    cardNumberMessage: types.maybeNull(types.string),
    cardExpiryMessage: types.maybeNull(types.string),
    cardCvcMessage: types.maybeNull(types.string),
    threeDSecure: types.optional(StripeThreeDSecureStore, {})
  })
  // volatile are things that are not serialized or tracked by MST. The things I store here are object pointers to created payment instances. These pointers can not be serialized, so they are referenced here.
  .volatile(self => ({
    client: null,
    elements: null,

    cardNumberElement: null,
    cardExpiryElement: null,
    cardCvcElement: null
  }))
  .views(self => ({
    get isValid() {
      return (
        self.cardNumberIsValid && self.cardExpiryIsValid && self.cardCvcIsValid
      )
    }
  }))
  .actions(self => ({
    initialize: flow(function* initGen() {
      self.isReady = false
      yield loadScript()
      self.buildClient()
      self.buildHostedFields()
      self.isReady = true
    }),
    setClientToken(clientToken) {
      self.clientToken = clientToken
    },
    setClient(clientReturn) {
      self.client = clientReturn
    },
    setElements(elementsReturn) {
      self.elements = elementsReturn
    },
    buildClient() {
      // eslint-disable-next-line no-undef
      const clientReturn = Stripe(process.env.STRIPE_PUBLIC_KEY)
      self.setClient(clientReturn)
      const elementsReturn = self.client.elements()
      self.setElements(elementsReturn)
    },
    setStripeElement(fieldName, elementResponse) {
      const name = `${fieldName}Element`
      self[name] = elementResponse
    },
    buildField(fieldName) {
      const options = { ...DEFAULT_ELEMENT_OPTIONS }
      options.placeholder = PLACEHOLDERS[fieldName]
      const elementResponse = self.elements.create(fieldName, options)
      self.setStripeElement(fieldName, elementResponse)
      elementResponse.mount(`#${fieldName}`)
      elementResponse.on('change', event =>
        self.validityCheckEventHandler(event, fieldName)
      )
    },
    buildHostedFields() {
      const elements = ['cardNumber', 'cardExpiry', 'cardCvc']
      elements.forEach(elementName => {
        self.buildField(elementName)
      })
    },
    tokenize() {
      return self.client
          .createPaymentMethod({
            type: 'card',
            card: self.cardNumberElement,
          })
          .then(result => {
            if (result.error) return Promise.reject(result.error)
            return result.paymentMethod.id
          })
    },
    confirm3DSecureAuthentication(clientSecret) {
      return self.client.confirmCardSetup(clientSecret, {
        payment_method: {
          card: self.cardNumberElement
        }
      })
    },
    validityCheckEventHandler(event, fieldName) {
      const isValidName = `${fieldName}IsValid`
      const messageName = `${fieldName}Message`
      if (event.error) {
        self[isValidName] = false
        self[messageName] = event.error.message
      } else if (event.empty) {
        self[messageName] = null
        self[isValidName] = false
      } else if (event.complete) {
        self[messageName] = null
        self[isValidName] = true
      } else {
        self[messageName] = null
        self[isValidName] = false
      }
    },
    teardown() {
      self.isReady = false
      self.cardNumberIsValid = false
      self.cardExpiryIsValid = false
      self.cardCvcIsValid = false
      if (self.hostedFields) {
        self.hostedFields.teardown()
        self.hostedFields = null
      }
    },
    validateForm() {
      if(self.cardNumberElement) {
        if(self.cardNumberElement._empty) {
          self.cardNumberMessage = "A card number is required."
        }
      }
      if(self.cardCvcElement) {
        if(self.cardCvcElement._empty) {
          self.cardCvcMessage = "A card cvc/cvv code is required."
        }
      }
      if(self.cardExpiryElement) {
        if(self.cardExpiryElement._empty) {
          self.cardExpiryMessage = "An expiration date is required."
        }
      }
    }
  }))

export default StripeStore
